import { useEffect, useRef, useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { useQuery } from "@tanstack/react-query";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseButton from "../../../components/ui/BaseButton";
import { fetchCompanyUsers } from "../../../services/api/company";
import { Chip } from "@mui/material";
import AddEditCompanyUserModal from "./components/AddEditCompanyUserModal";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import { debounce, formatPhoneNumber } from "../../../utils/helpers";
import BaseDropdownV2 from "../../../components/ui/BaseDropdownV2";
import ConfirmDeactivateUserModal from "./components/ConfirmDeactivateCompnayUserModal";
import ConfirmActivateUserModal from "./components/ConfirmActivateUserModal";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import CompleteProfileModal from "../../WorkJobDetails/CompleteProfileModal";
import useAuth from "../../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import { deleteUser } from "../../../services/api/user";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import UserProfileLink from "../../../components/ui/UserProfileLink";

const menuOptions = [
  {
    label: "Edit",
    value: "EDIT",
  },
  {
    label: "Activate",
    value: "APPROVED",
  },
  {
    label: "Deactivate",
    value: "DEACTIVATED",
  },
  {
    label: "Delete",
    value: "DELETE",
  },
];

const CompanyUsersTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isAddEditCompanyUserOpen, setIsAddEditCompanyUserOpen] = useState(false);
  const [isConfrimDeactivateUserOpen, setIsConfrimDeactivateUserOpen] = useState(false);
  const [isConfrimDeleteUserOpen, setIsConfrimDeleteUserOpen] = useState(false);
  const [isConfrimActivateUserOpen, setIsConfrimActivateUserOpen] = useState(false);
  const [companyUsersList, setCompanyUsersList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const { userData } = useCurrentUser();
  const [isProfileCompleted, setIsProfileCompleted] = useState(false);
  const { authRole } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [isDeleting, setIsDeleting] = useState(null);
  const baseQueryParams = {
    limit: 20,
    page: 1,
    search: "",
  };
  const [queryParams, setQueryParams] = useState(baseQueryParams);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });

  const [rowCount, setRowCount] = useState(null);
  const {
    data: companyUsers,
    isPending: isCompanyUsersApiLoading,
    error: isCompanyUsersApiError,
    refetch,
  } = useQuery({
    queryKey: ["companyUsers", queryParams.limit, queryParams.page, queryParams.search],
    queryFn: () => fetchCompanyUsers(queryParams),
  });

  useEffect(() => {
    if (companyUsers?.data) {
      setCompanyUsersList(companyUsers.data?.users);
      setRowCount(companyUsers.data.totalCount);
    }
  }, [companyUsers]);

  const handleDesignationClick = (designation) => {
    navigate(
      `/company/${designation?.designation
        ?.toLowerCase()
        ?.replace(/\s+/g, "")}/permissions/${designation?.id}`,
    );
  };
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    if (!deleteReason?.trim()) {
      dispatch(
        showToast({
          message: "Please provide a reason before deleting the user.",
          type: "error",
        }),
      );
      return;
    }
    try {
      setIsDeleting(true);
      await deleteUser(selectedUser.id, deleteReason);
      setIsConfrimDeleteUserOpen(false);
      refetch();
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.5,
      minWidth: 80,
      renderCell: (params) => <p className="text-primary cursor-default">{params?.value}</p>,
    },
    {
      field: "firstName",
      headerName: "Full Name",
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) => (
        <UserProfileLink userId={params.row?.id}>
          {params.value} {params.row?.lastName}
        </UserProfileLink>
      ),
    },
    {
      field: "assignedDesignation",
      headerName: "Designation",
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) => (
        <p
          className="capitalize text-primary cursor-pointer"
          onClick={() => handleDesignationClick(params?.value)}
        >
          {params?.value?.designation}
        </p>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 0.7,
      minWidth: 220,
      renderCell: (params) => <p>{params.value}</p>,
    },
    {
      field: "phoneNumber",
      headerName: "Phone Number",
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) => <p>{formatPhoneNumber(params.value)}</p>,
    },
    {
      field: "addressLine1",
      headerName: "Address",
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) => <p>{params.row?.personalAddress?.addressLine1}</p>,
    },
    {
      field: "country",
      headerName: "Country",
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) => <p>{params.row?.personalAddress?.country}</p>,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) =>
        params.value === "APPROVED" ? (
          <Chip label="Active" color="success" size="small" className="w-24 justify-center" />
        ) : params.value === "DEACTIVATED" ? (
          <Chip label="In-Active" color="error" size="small" className="w-24 justify-center" />
        ) : (
          <Chip
            label="Invited"
            color="primary"
            size="small"
            variant="outlined"
            className="w-24 justify-center"
          />
        ),
    },

    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 90,
      renderCell: (params) => {
        const userStatus = params?.row?.status;
        const filteredMenuOptions = menuOptions.filter((item) => {
          if (userStatus === "PENDING") {
            if (item.value === "APPROVED" || item.value === "DEACTIVATED") {
              return false;
            }
          }
          if (userStatus === "APPROVED" && (item.value === "APPROVED" || item.value === "DELETE")) {
            return false;
          }

          if (userStatus === "DEACTIVATED" && item.value === "DEACTIVATED") {
            return false;
          }
          if (
            userStatus === "PENDING" &&
            (item.value === "INVITED" || item.value === "APPROVED" || item.value === "EDIT")
          ) {
            return false;
          }

          return true;
        });

        return (
          <BaseDropdownV2
            menuOptions={filteredMenuOptions}
            triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
            onItemClick={(item) => handleItemClick(item, params?.row)}
          />
        );
      },
    },
  ];

  const updateQueryParamsDebounced = useRef(
    debounce((query) => {
      setQueryParams((prev) => ({
        ...prev,
        search: query.trim() || "",
        page: 1,
      }));
    }, 500),
  ).current;

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateQueryParamsDebounced(query);
  };

  const handlePaginationChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);

    setQueryParams((prev) => ({
      ...prev,
      page: newPaginationModel.page + 1,
      limit: newPaginationModel.pageSize,
    }));
  };
  const handleItemClick = async (value, data) => {
    setSelectedUser(data);
    if (value === "APPROVED") {
      setIsConfrimActivateUserOpen(true);
    } else if (value === "EDIT") {
      setIsAddEditCompanyUserOpen(true);
    } else if (value === "DEACTIVATED") {
      setIsConfrimDeactivateUserOpen(true);
    } else {
      setDeleteReason("");
      setIsConfrimDeleteUserOpen(true);
    }
  };

  return (
    <>
      <BaseCard elevation={0} className="py-4 px-5 rounded-xl shadow-lg ">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 w-full">
          <div className="w-full sm:w-2/6">
            <BaseTextField
              placeholder="Search by name, email or ID"
              onChange={handleSearchChange}
              startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
            />
          </div>

          <BaseButton
            color="primary"
            className="!py-[5px] whitespace-nowrap w-full sm:w-auto"
            onClick={() => {
              if (userData?.status !== "APPROVED") {
                setIsProfileCompleted(true);
                return;
              }
              setIsAddEditCompanyUserOpen(true);
              setSelectedUser(null);
            }}
          >
            Invite New User
          </BaseButton>
        </div>
      </BaseCard>

      <BaseDataTableV2
        className="max-h-[calc(100vh-320px)] overflow-auto"
        columns={columns}
        data={companyUsersList}
        isLoading={isCompanyUsersApiLoading}
        isCellEditable={(params) => false}
        hideFooter={false}
        pagination
        rowCount={rowCount}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationModel={paginationModel} // Connect paginationModel
        onPaginationModelChange={handlePaginationChange} // Handle page and pageSize change
        paginationMode="server"
      />

      {isAddEditCompanyUserOpen && (
        <AddEditCompanyUserModal
          open={isAddEditCompanyUserOpen}
          close={setIsAddEditCompanyUserOpen}
          setCompanyUsersList={setCompanyUsersList}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
        />
      )}
      {isConfrimDeactivateUserOpen && (
        <ConfirmDeactivateUserModal
          deactivateModalOpen={isConfrimDeactivateUserOpen}
          setDeactivateModalOpen={setIsConfrimDeactivateUserOpen}
          userId={selectedUser?.id}
          refetchUsers={refetch}
        />
      )}
      {isConfrimActivateUserOpen && (
        <ConfirmActivateUserModal
          activateModalOpen={isConfrimActivateUserOpen}
          setActivateModalOpen={setIsConfrimActivateUserOpen}
          userId={selectedUser?.id}
          refetchUsers={refetch}
        />
      )}
      {isConfrimDeleteUserOpen && (
        <BaseConfirmModal
          open={isConfrimDeleteUserOpen}
          onClose={() => setIsConfrimDeleteUserOpen(false)}
          onConfirm={handleDeleteUser}
          isLoading={isDeleting}
          title="Are you sure you want to delete this user?"
          description={
            <>
              <BaseTextField
                label="Reason"
                name="reason"
                placeholder="Reason"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                labelShrink={false}
                optional={false}
                isClearable
                isDefaultField={false}
              />
              <p className="mt-2 ">This action cannot be undone.</p>
            </>
          }
          confirmText="Delete"
          confirmButtonColor="error"
        />
      )}
      {isProfileCompleted && (
        <CompleteProfileModal
          isProfileCompleted={isProfileCompleted}
          setIsProfileCompleted={setIsProfileCompleted}
          status={userData?.status}
          authRole={authRole}
        />
      )}
    </>
  );
};

export default CompanyUsersTable;
